import React, { useState, useCallback } from 'react';
import {
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	Textarea,
	Select,
	SelectItem,
	addToast
} from '@heroui/react';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import DiscrepanciesApi from '../../../api/discrepanciesApi.js';

/**
 * DiscrepancyAddScreen — in-modal Add form (Phase 2.1 Sub 12).
 *
 * Legacy parity: `calendar.discrepancy-add.js`. Submits via
 * `DiscrepanciesApi.addDiscrepancy(payload, orgUniqueOverride)` (Phase 2.1
 * Sub 11 added the override param for cross-org events).
 *
 * Props:
 *   - `aircraftId` — the aircraft to attach the discrepancy to.
 *   - `orgUnique` — event's org (cross-org safe).
 *   - `mechanics` — optional `[{id, name, surname}]` list for the assign-to
 *     picker. Empty/missing → text-input fallback.
 *   - `onBack(refresh?)` — return to the list. If `refresh` is true, parent
 *     should re-fetch the discrepancies list.
 */
function DiscrepancyAddScreen({ aircraftId, orgUnique, mechanics, onBack }) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [priority, setPriority] = useState('2'); // Medium default
	const [assignedTo, setAssignedTo] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = useCallback(async () => {
		if (!name.trim()) {
			addToast({ title: 'Name is required', color: 'warning' });
			return;
		}
		// Rem18 Task 3: backend `AddDiscrepancyRequestDto.php:50-57` has
		// `Assert\NotBlank` + length constraint on description. Block
		// client-side so users don't hit a 422 after submit.
		if (!description.trim()) {
			addToast({ title: 'Description is required', color: 'warning' });
			return;
		}
		if (!aircraftId) {
			addToast({
				title: 'No aircraft',
				description: 'This event has no aircraft to attach a discrepancy to.',
				color: 'danger'
			});
			return;
		}
		setSubmitting(true);
		try {
			const payload = {
				aircraftId,
				name: name.trim(),
				description: description.trim(),
				priority: Number(priority),
				assignTo: assignedTo ? Number(assignedTo) : null
			};
			const res = await DiscrepanciesApi.addDiscrepancy(payload, orgUnique);
			const body = res?.data ?? res;
			if (body?.success === false) {
				const msg = (body.error && body.error[0]) || 'Add failed';
				addToast({ title: 'Add failed', description: msg, color: 'danger' });
				return;
			}
			addToast({ title: 'Discrepancy added', color: 'success' });
			onBack?.(true);
		} catch (err) {
			const msg = err?.response?.data?.error?.[0] || err?.message || 'Add failed';
			addToast({ title: 'Add failed', description: msg, color: 'danger' });
		} finally {
			setSubmitting(false);
		}
	}, [name, description, priority, assignedTo, aircraftId, orgUnique, onBack]);

	const mechanicList = Array.isArray(mechanics) ? mechanics : [];

	return (
		<>
			<ModalHeader className="flex items-center gap-3 border-b border-default-200 pr-10">
				<Button
					isIconOnly
					size="sm"
					variant="light"
					onPress={() => onBack?.()}
					aria-label="Back to list"
				>
					<ChevronLeft className="w-4 h-4" />
				</Button>
				<span
					className="text-lg font-bold flex-1"
					style={{ color: 'hsl(var(--heroui-foreground))' }}
				>
					Add Discrepancy
				</span>
			</ModalHeader>

			<ModalBody className="pt-4">
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
					className="space-y-3"
				>
					<Input
						label="Name"
						isRequired
						value={name}
						onValueChange={setName}
						placeholder="Short summary"
					/>
					<Textarea
						label="Description"
						isRequired
						value={description}
						onValueChange={setDescription}
						minRows={3}
						placeholder="Describe the discrepancy"
					/>
					<Select
						label="Priority"
						isRequired
						selectedKeys={[priority]}
						onSelectionChange={keys => {
							const next = Array.from(keys)[0];
							if (next) setPriority(String(next));
						}}
					>
						<SelectItem key="1">High</SelectItem>
						<SelectItem key="2">Medium</SelectItem>
						<SelectItem key="3">Low</SelectItem>
					</Select>
					{mechanicList.length > 0 ? (
						<Select
							label="Assigned To"
							selectedKeys={assignedTo ? [String(assignedTo)] : []}
							onSelectionChange={keys => {
								const next = Array.from(keys)[0];
								setAssignedTo(next ? String(next) : '');
							}}
						>
							{mechanicList.map(m => (
								<SelectItem key={String(m.id)}>
									{[m.name, m.surname].filter(Boolean).join(' ') || `Mechanic #${m.id}`}
								</SelectItem>
							))}
						</Select>
					) : (
						<Input
							label="Assigned To"
							placeholder="Mechanic user id (optional)"
							value={assignedTo}
							onValueChange={setAssignedTo}
						/>
					)}
				</motion.div>
			</ModalBody>

			<ModalFooter className="justify-between gap-2 flex-wrap border-t border-default-200 pt-3">
				<Button size="sm" variant="bordered" onPress={() => onBack?.()}>
					Cancel
				</Button>
				<Button size="sm" color="success" isLoading={submitting} onPress={handleSubmit}>
					Add
				</Button>
			</ModalFooter>
		</>
	);
}

export default DiscrepancyAddScreen;
